import { Component, OnInit, Input, input } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl, FormControl } from '@angular/forms';
import { LovDataService } from 'src/app/modules/shared/services/lov-data/lov-data.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';
import { ModalController } from '@ionic/angular';
import { MEAL_PLANS } from '../../shared/constants/api-constants';
import { InputValidationsService } from '../../shared/services/validations/input-validations.service';
import { InventoryHotelsService } from 'src/app/business-service/inventory/inventory-hotels.service';
import { ComponentReloadService } from '../../shared/services/reload/component-reload.service';
import { GlobalService } from '../../shared/services/global/global.service';

@Component({
  selector: 'app-add-hotel',
  templateUrl: './add-hotel.component.html',
  styleUrls: ['./add-hotel.component.scss'],
})
export class AddHotelComponent implements OnInit {
  @Input() hotelId: any;
  @Input() hotelData: any;
  @Input() editHotelId: any;

  hotelPricingForm!: FormGroup;

  accommodationType: any;
  MEAL_PLAN = MEAL_PLANS;
  roomType: any = [];
  roomCategory: any = [];
  meals: any = [];

  todayDate: string = new Date().toISOString().split('T')[0];
  startDateValue: string = "";

  facilitiesOptions: any[] = [
    { name: 'Free Wifi', key: 'Free Wifi' },
    { name: 'Bar/Lounge', key: 'Bar/Lounge' },
    { name: 'Gym/Fitness', key: 'Gym/Fitness' },
    { name: 'Swimming pool', key: 'Swimming pool' },
    { name: 'Live Music', key: 'Live Music' },
  ];

  currencyLov: any = [];
  defaultCurrency: any;

  constructor(
    private _fb: FormBuilder,
    private _lovDataService: LovDataService,
    private _modalController: ModalController,
    private _validationService: InputValidationsService,
    private _hotelsService: InventoryHotelsService,
    private _notificationServices: ResponseNotificationService,
    private _reloadService: ComponentReloadService,
    private _globalService: GlobalService
  ) { }

  async ngOnInit() {
    let organizationDetails = await this._globalService.getLoggedInOrgDetails();
    this.defaultCurrency = organizationDetails.organizationDetails.currencyCode;

    this.hotelPricingForm = this._fb.group({
      hotelId: [null],

      hotelDetails: this._fb.group({
        location: ['', Validators.required],
        hotelName: ['', Validators.required],
        hotelCategory: ['', Validators.required],
        hotelWebsite: ['', this._validationService.validateWebsiteUrl],
        availableNights: [0, Validators.required],
        address1: ['', Validators.required],
        address2: [''],
        groupedAddress: [null],
        firstName: ['', [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          Validators.pattern('^[a-zA-Z ]*$')
        ]],
        lastName: ['', [
          Validators.minLength(2),
          Validators.maxLength(50),
          Validators.pattern('^[a-zA-Z ]*$')
        ]],
        phoneNumber: [{ countryCode: '+91', phone: '' }],
        contactEmail: ['', [
          Validators.required,
          this._validationService.emailValidator()
        ]],
        nearByPlace: [''],
        facilities: new FormControl([]),
      }),

      seasons: this._fb.array([
        this.createSeasonFormGroup()
      ]),

      calculationSettings: this._fb.group({
        autoCalculationEnabled: [false],
        mapDifference: [0],
        apDifference: [0]
      }),

      rooms: this._fb.array([
        this.createRoomFormGroup()
      ]),

      currency: [this.defaultCurrency, [Validators.required]],
      status: ['Active'],
      notes: [''],
      id: [null],
      ownershipRole: [''],
      organizationId: [null]
    });

    this.currencyLov = await this._globalService.loadLovData("CurrencyCode");
    this.accommodationType = await this._globalService.loadLovData("HotelType");
    this.roomCategory = await this._globalService.loadLovData("RoomCategory");
    this.roomType = await this._globalService.loadLovData("RoomType");
    this.meals = await this._globalService.loadLovData("Meals");

    // If hotelData exists, patch the form
    if (this.editHotelId) {
      this.getHotelDetails();
    }
  }

  // Seasons FormArray
  get seasons(): FormArray {
    return this.hotelPricingForm.get('seasons') as FormArray;
  }

  // Rooms FormArray
  get rooms(): FormArray {
    return this.hotelPricingForm.get('rooms') as FormArray;
  }

  // Create season form group
  createSeasonFormGroup(seasonData: any = null): FormGroup {
    let group = this._fb.group({
      title: [seasonData?.title || '', [Validators.required]],
      startDate: [seasonData?.startDate || null, [Validators.required]],
      endDate: [seasonData?.endDate || null, [Validators.required]]
    });

    return group;
  }

  // Add season
  addSeason() {
    this.seasons.push(this.createSeasonFormGroup());

    this.rooms.controls.forEach((room: AbstractControl) => {
      const pricing = room.get('pricing') as FormArray;

      pricing.push(
        this.roomPricingFormGroup(pricing.length)
      );
    });

    this.syncSeasonIndexes();
  }

  // Remove season
  removeSeason(index: number) {
    if (this.seasons.length === 1) {
      return;
    }

    this.seasons.removeAt(index);

    this.rooms.controls.forEach((room: AbstractControl) => {
      const pricing = room.get('pricing') as FormArray;

      if (index < pricing.length) {
        pricing.removeAt(index);
      }
    });

    this.syncSeasonIndexes();
  }

  // Room Details and Pricing Section Form Group
  createRoomFormGroup(roomData: any = null): FormGroup {
    let group = this._fb.group({
      roomCategory: [
        roomData?.roomCategory || '',
        [Validators.required]
      ],

      baseOccupancy: [
        roomData?.baseOccupancy || 0,
        [
          Validators.required,
          this._validationService.valueNotZeroValidator()
        ]
      ],

      extraBaseOccupancy: [
        roomData?.extraBaseOccupancy || 0,
        []
      ],

      pricing: this._fb.array([
        this.roomPricingFormGroup(0)
      ])
    });

    return group;
  }

  // Room Pricing Form Group
  roomPricingFormGroup(seasonIndex: number = 0, pricingData: any = null): FormGroup {
    let group = this._fb.group({
      seasonIndex: [seasonIndex],

      ep: this._fb.group({
        baseRate: [pricingData?.cp?.baseRate ?? 0],
        aweb: [pricingData?.cp?.aweb ?? 0],
        cweb: [pricingData?.cp?.cweb ?? 0],
        cnb: [pricingData?.cp?.cnb ?? 0],
      }),

      cp: this._fb.group({
        baseRate: [pricingData?.cp?.baseRate ?? 0],
        aweb: [pricingData?.cp?.aweb ?? 0],
        cweb: [pricingData?.cp?.cweb ?? 0],
        cnb: [pricingData?.cp?.cnb ?? 0],
      }),

      map: this._fb.group({
        baseRate: [pricingData?.map?.baseRate ?? 0],
        aweb: [pricingData?.map?.aweb ?? 0],
        cweb: [pricingData?.map?.cweb ?? 0],
        cnb: [pricingData?.map?.cnb ?? 0],
      }),

      ap: this._fb.group({
        baseRate: [pricingData?.ap?.baseRate ?? 0],
        aweb: [pricingData?.ap?.aweb ?? 0],
        cweb: [pricingData?.ap?.cweb ?? 0],
        cnb: [pricingData?.ap?.cnb ?? 0],
      }),
    });

    group.get("cp.baseRate")?.valueChanges.subscribe(value => {
      const settings = this.hotelPricingForm?.get("calculationSettings")?.value;
      if (settings?.autoCalculationEnabled) {
        const mapCost = +value + +(settings.mapDifference);
        group.get("map.baseRate")?.setValue(mapCost);
        group.get("map.baseRate")?.updateValueAndValidity();
      }
    });
    group.get("cp.aweb")?.valueChanges.subscribe(value => {
      const settings = this.hotelPricingForm?.get("calculationSettings")?.value;
      if (settings?.autoCalculationEnabled) {
        const mapCost = +value + +(settings.mapDifference);
        group.get("map.aweb")?.setValue(mapCost);
        group.get("map.aweb")?.updateValueAndValidity();
      }
    });
    group.get("cp.cweb")?.valueChanges.subscribe(value => {
      const settings = this.hotelPricingForm?.get("calculationSettings")?.value;
      if (settings?.autoCalculationEnabled) {
        const mapCost = +value + +(settings.mapDifference);
        group.get("map.cweb")?.setValue(mapCost);
        group.get("map.cweb")?.updateValueAndValidity();
      }
    });
    group.get("cp.cnb")?.valueChanges.subscribe(value => {
      const settings = this.hotelPricingForm?.get("calculationSettings")?.value;
      if (settings?.autoCalculationEnabled) {
        const mapCost = +value + +(settings.mapDifference);
        group.get("map.cnb")?.setValue(mapCost);
        group.get("map.cnb")?.updateValueAndValidity();
      }
    });

    group.get("map.baseRate")?.valueChanges.subscribe(value => {
      const settings = this.hotelPricingForm?.get("calculationSettings")?.value;
      if (settings?.autoCalculationEnabled) {
        const apCost = +value + +(settings.apDifference);
        group.get("ap.baseRate")?.setValue(apCost);
        group.get("ap.baseRate")?.updateValueAndValidity();
      }
    });
    group.get("map.aweb")?.valueChanges.subscribe(value => {
      const settings = this.hotelPricingForm?.get("calculationSettings")?.value;
      if (settings?.autoCalculationEnabled) {
        const apCost = +value + +(settings.apDifference);
        group.get("ap.aweb")?.setValue(apCost);
        group.get("ap.aweb")?.updateValueAndValidity();
      }
    });
    group.get("map.cweb")?.valueChanges.subscribe(value => {
      const settings = this.hotelPricingForm?.get("calculationSettings")?.value;
      if (settings?.autoCalculationEnabled) {
        const apCost = +value + +(settings.apDifference);
        group.get("ap.cweb")?.setValue(apCost);
        group.get("ap.cweb")?.updateValueAndValidity();
      }
    });
    group.get("map.cnb")?.valueChanges.subscribe(value => {
      const settings = this.hotelPricingForm?.get("calculationSettings")?.value;
      if (settings?.autoCalculationEnabled) {
        const apCost = +value + +(settings.apDifference);
        group.get("ap.cnb")?.setValue(apCost);
        group.get("ap.cnb")?.updateValueAndValidity();
      }
    });

    return group;
  }

  // Add Room
  addRoom() {
    const room = this.createRoomFormGroup();

    const pricing = room.get('pricing') as FormArray;

    pricing.clear();

    this.seasons.controls.forEach(
      (season: AbstractControl, index: number) => {
        pricing.push(
          this.roomPricingFormGroup(index)
        );
      }
    );

    this.rooms.push(room);

    this.syncSeasonIndexes();
  }

  // Remove Room
  removeRoom(index: number) {
    if (this.rooms.length === 1) {
      return;
    }

    this.rooms.removeAt(index);
  }

  // Get pricing FormArray for a room
  getRoomPricing(roomIndex: number): FormArray {
    return this.rooms
      .at(roomIndex)
      .get('pricing') as FormArray;
  }

  // Synchronize seasonIndex with pricing FormArray index
  private syncSeasonIndexes(): void {
    this.rooms.controls.forEach((room: AbstractControl) => {
      const pricing = room.get('pricing') as FormArray;

      pricing.controls.forEach((pricingGroup: AbstractControl, index: number) => {
        pricingGroup.get('seasonIndex')?.setValue(index, {
          emitEvent: false
        });
      });
    });
  }

  // Get season title based on season index
  getSeasonTitle(seasonIndex: any): string {
    const index = Number(seasonIndex);

    if (
      isNaN(index) ||
      !this.seasons ||
      !this.seasons.at(index)
    ) {
      return `Season ${index + 1}`;
    }

    return this.seasons.at(index).get('title')?.value || `Season ${index + 1}`;
  }

  // Serialize the grouped address fields
  serializeData(formData: any) {
    const serializeFormData = {
      ...formData
    };

    return serializeFormData;
  }

  // Edit hotel details and fetch existing details
  getHotelDetails() {
    if (this.editHotelId) {
      this._hotelsService.getSpecificHotel(this.editHotelId).subscribe({
        next: (response: any) => {
          this.patchHotelForm(response);
        },
        error: (error: any) => {
          console.error("Error fetching hotel details:", error);
        }
      });
    } else {
      console.error("No hotelId found in route parameters.");
    }
  }

  // Form Submission (Save)
  onSubmit(isSaveAndNew: boolean = false) {
    this.hotelPricingForm.markAllAsTouched();

    if (this.hotelPricingForm.invalid) {
      this._globalService.customAlert("Form is invalid, please fill all required fields.");
      return;
    }

    this.syncSeasonIndexes();

    const formData = this.serializeData(this.hotelPricingForm.value);

    this._hotelsService.AddInventoryHotel(formData).subscribe({
      next: async (data: any) => {
        if (this.editHotelId)
          this._notificationServices.showMessage("Hotel details updated successfully");
        else
          this._notificationServices.showMessage("Hotel details Added successfully");

        this._reloadService.triggerInvenotryHotelsReload();

        if (isSaveAndNew) {
          this.hotelPricingForm.reset();

          this.hotelPricingForm.patchValue({
            hotelDetails: {
              groupedAddress: {
                city: null,
                state: null,
                country: null,
                zipCode: null
              }
            }
          });

          return;
        }

        this.closeModal();
      },

      error: (err) => {
        console.error("Error updating hotel data:", err);
        this._notificationServices.showMessage("Error updating hotel details", true);
      }
    });
  }

  // Patch the form with data
  private patchHotelForm(response: any): void {
    if (!response) return;

    // Patch normal fields
    this.hotelPricingForm.patchValue({
      hotelId: response.hotelId,
      id: response.id,
      notes: response.notes,
      status: response.status,
      currency: response.currency,
      organizationId: response.organizationId,

      hotelDetails: {
        location: response.hotelDetails?.location || "",
        hotelName: response.hotelDetails?.hotelName || "",
        hotelCategory: response.hotelDetails?.hotelCategory || "",
        hotelWebsite: response.hotelDetails?.hotelWebsite || "",
        availableNights: response.hotelDetails?.availableNights || 0,
        address1: response.hotelDetails?.address1 || "",
        address2: response.hotelDetails?.address2 || "",

        groupedAddress: {
          country: response.hotelDetails?.country || "",
          state: response.hotelDetails?.state || "",
          city: response.hotelDetails?.city || "",
          zipCode: response.hotelDetails?.zipCode || ""
        },

        firstName: response.hotelDetails?.firstName || "",
        lastName: response.hotelDetails?.lastName || "",
        phoneNumber: response.hotelDetails?.phoneNumber || null,
        contactEmail: response.hotelDetails?.contactEmail || "",
        nearByPlace: response.hotelDetails?.nearByPlace || "",
        facilities: response.hotelDetails?.facilities || []
      }
    });

    // Patch calculation settings
    this.hotelPricingForm.get('calculationSettings')?.patchValue({
      autoCalculationEnabled:
        response.calculationSettings?.autoCalculationEnabled ?? false,

      mapDifference:
        response.calculationSettings?.mapDifference ?? 0,

      apDifference:
        response.calculationSettings?.apDifference ?? 0
    });

    // Patch Seasons
    this.seasons.clear();

    if (response.seasons && response.seasons.length > 0) {
      response.seasons.forEach((season: any) => {
        this.seasons.push(
          this.createSeasonFormGroup(season)
        );
      });
    } else {
      this.seasons.push(
        this.createSeasonFormGroup()
      );
    }

    // Patch Rooms
    this.rooms.clear();

    if (response.rooms && response.rooms.length > 0) {
      response.rooms.forEach((roomData: any) => {
        const room = this._fb.group({
          roomCategory: [roomData.roomCategory || '', [Validators.required]],
          baseOccupancy: [
            roomData.baseOccupancy || 0,
            [
              Validators.required,
              this._validationService.valueNotZeroValidator()
            ]
          ],
          extraBaseOccupancy: [
            roomData.extraBaseOccupancy || 0,
            []
          ],
          pricing: this._fb.array([])
        });
        const pricing = room.get('pricing') as FormArray;
        if (roomData.pricing && roomData.pricing.length > 0) {
          roomData.pricing.forEach((pricingData: any, pricingIndex: number) => {
            pricing.push(
              this.roomPricingFormGroup(
                pricingIndex,
                pricingData
              )
            );
          });
        } else {
          // Create pricing for all available seasons
          this.seasons.controls.forEach(
            (season: AbstractControl, seasonIndex: number) => {
              pricing.push(
                this.roomPricingFormGroup(seasonIndex)
              );
            }
          );
        }
        this.rooms.push(room);
      });
    } else {
      this.addRoom();
    }

    this.syncSeasonIndexes();
    // Patch ownership role if available
    this.hotelPricingForm.get('ownershipRole')?.patchValue(
      response.ownershipRole || ''
    );
  }

  // Close button
  closeModal() {
    this._modalController.dismiss();
  }

  formDataModified(): boolean {
    return (
      (this.hotelPricingForm?.dirty || this.hotelPricingForm?.invalid) &&
      this.hotelPricingForm?.touched
    );
  }
}